import React, { useState } from "react";
import clsx from "clsx";
import { t } from "ttag";
import { useDropzone } from "react-dropzone";
import { BaseDialog, Button, CloudUploadIcon, DeleteIcon, ExcelIcon, Spinner, Typography } from '@coolremote/shared';
import { useStoreActions, useStoreState } from "../../models/RootStore";
import { IZoneMap } from "../../models/Zones";
import {
  ACCEPTED_EXTENSIONS,
  EXPECTED_HEADERS,
  IResultMessage,
  SAMPLE_ROWS,
  buildPreviewMessages,
  buildRowContext,
  categoryFromType,
  classifyRow,
  downloadSampleTemplate,
  getUnitType,
  parseFile,
  summarizeByCategory,
  validateStructure,
} from "./tenantListIO";

import useStyles from "./ImportTenantListModal.style";

const EXPECTED_HEADERS_STRING = EXPECTED_HEADERS.join(', ');

interface IImportTenantListModalProps {
  siteId: string;
  siteName: string;
  tenants: IZoneMap;
  isControlBasedPPD: boolean;
  onImportComplete: () => void;
  onClose: () => void;
}

const ImportTenantListModal = ({
  siteId,
  tenants,
  isControlBasedPPD,
  onImportComplete,
  onClose
}: IImportTenantListModalProps) => {
  const classes = useStyles();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [file, setFile] = useState<File | null>(null);
  const [errorText, setErrorText] = useState<string>('');
  const [parsedRows, setParsedRows] = useState<string[][] | null>(null);
  const [preview, setPreview] = useState<IResultMessage[] | null>(null);
  const [results, setResults] = useState<IResultMessage[] | null>(null);

  const allUnits = useStoreState((state) => state.units.allUnits);
  const { createZone, updateZone } = useStoreActions((action) => action.zones);
  const unitType = getUnitType(isControlBasedPPD);

  const resetFileState = () => {
    setFile(null);
    setErrorText('');
    setParsedRows(null);
    setPreview(null);
    setResults(null);
  };

  const handleFile = async (droppedFile: File) => {
    setErrorText('');
    setParsedRows(null);
    setPreview(null);
    setResults(null);

    if (droppedFile.size > 5 * 1024 * 1024) {
      setErrorText(t`File size exceeds the maximum allowed size (5MB).`);
      setFile(null);
      return;
    }

    const extension = droppedFile.name.slice(droppedFile.name.lastIndexOf('.')).toLowerCase();
    if (!ACCEPTED_EXTENSIONS.includes(extension)) {
      setErrorText(t`Unsupported file type. Please upload a .csv or .xlsx file.`);
      setFile(null);
      return;
    }

    setFile(droppedFile);

    let rows: string[][];
    try {
      rows = await parseFile(droppedFile);
    } catch (error) {
      setErrorText(t`Bad file structure`);
      setFile(null);
      return;
    }

    const dataRows = validateStructure(rows);
    if (!dataRows) {
      setErrorText(t`Bad file structure`);
      setFile(null);
      return;
    }

    if (dataRows.length > 1000) {
      setErrorText(t`File exceeds the maximum allowed rows (1000).`);
      setFile(null);
      return;
    }

    const ctx = buildRowContext(tenants, allUnits, siteId, unitType);
    setParsedRows(dataRows);
    setPreview(buildPreviewMessages(dataRows, ctx));
  };

  const onDrop = (acceptedFiles: File[]) => {
    const droppedFile = acceptedFiles[0];
    if (!droppedFile) {
      setErrorText(t`No file was uploaded.`);
      return;
    }
    handleFile(droppedFile);
  };

  const { getRootProps, getInputProps } = useDropzone({ onDrop, accept: ACCEPTED_EXTENSIONS, maxFiles: 1 });

  const runImport = async () => {
    if (!parsedRows) {
      return;
    }

    setIsLoading(true);
    setResults([]);

    const ctx = buildRowContext(tenants, allUnits, siteId, unitType);
    const pushResult = (rowNumber: number, type: IResultMessage['type'], message: string) => {
      setResults((prev) => [...(prev || []), { rowNumber, type, category: categoryFromType(type), message }]);
    };

    const withUnit = (zone: any, unitId: string, present: boolean) => {
      const units = new Set(zone.units || []);
      if (present) units.add(unitId); else units.delete(unitId);
      return Array.from(units);
    };

    const persistZoneUnits = async (zone: any, unitId: string, present: boolean) => {
      const updatedZone = await updateZone({
        id: zone.id,
        data: { name: zone.name, description: zone.description, units: withUnit(zone, unitId, present) }
      });
      ctx.zonesById[updatedZone.id] = updatedZone;
      return updatedZone;
    };

    for (let i = 0; i < parsedRows.length; i++) {
      const rowNumber = i + 1;
      const outcome = classifyRow(rowNumber, parsedRows[i], ctx);

      if (outcome.kind !== 'ready') {
        pushResult(rowNumber, outcome.kind, outcome.message);
        continue;
      }

      try {
        let targetZoneId = outcome.targetZoneId;

        if (!targetZoneId) {
          const newZone = await createZone({
            data: {
              name: outcome.tenantName,
              units: [outcome.unit.id],
              description: '',
              site: siteId,
              ...(isControlBasedPPD && { controlBased: true })
            }
          });
          ctx.zonesById[newZone.id] = newZone;
          ctx.zoneIdByName[outcome.tenantKey] = newZone.id;
          targetZoneId = newZone.id;
          pushResult(rowNumber, 'good', t`Row #${rowNumber} - new tenant created: ${outcome.tenantName}`);

          if (outcome.currentZoneId && outcome.currentZoneId !== targetZoneId) {
            await persistZoneUnits(ctx.zonesById[outcome.currentZoneId], outcome.unit.id, false);
          }
        } else if (outcome.currentZoneId !== targetZoneId) {
          // no-op if the unit is already correctly assigned to this tenant
          if (outcome.currentZoneId) {
            await persistZoneUnits(ctx.zonesById[outcome.currentZoneId], outcome.unit.id, false);
          }
          await persistZoneUnits(ctx.zonesById[targetZoneId], outcome.unit.id, true);
        }

        ctx.unitZoneId[outcome.unit.id] = targetZoneId as string;
        pushResult(rowNumber, 'good', t`Row #${rowNumber} - Unit ${outcome.unitName} assigned to tenant ${outcome.tenantName}`);
      } catch (error) {
        pushResult(rowNumber, 'error', t`Row #${rowNumber} - Failed to assign unit: ${(error as Error).message}`);
      }
    }

    onImportComplete();
    setIsLoading(false);
    onClose();
  };

  const isDone = !isLoading && !!results;

  const displayedRows = results || preview;
  const aggregateSummary = displayedRows ? summarizeByCategory(displayedRows) : '';

  return (
    <BaseDialog
      open={true}
      onClose={onClose}
      staticBackdrop
      disabled={isLoading}
      title={t`Import Unit/Tenant info from file`}
      className={classes.dialogRoot}
      style={{ width: 'min(700px, 90vw)', maxWidth: 'none' }}
      paperProps={{ style: { width: '100%', height: '600px', maxHeight: '600px' } }}
      cancelLabel={isDone ? t`Close` : t`Cancel`}
      confirmLabel={t`Import`}
      confirmDisabled={!parsedRows || !!errorText || !!results}
      loading={isLoading}
      onConfirm={runImport}
    >
      <div className={classes.contentWrapper}>
        <div className={classes.headerText}>
          <Typography className={classes.subtitleText}>
            {t`Need help formatting your file?`}{' '}
            <button type="button" className={classes.sampleTemplateLink} onClick={downloadSampleTemplate}>
              {t`Download sample template`}
            </button>
          </Typography>
          <Typography className={classes.subtitleText}>
            <strong>{t`Required File Structure:`}</strong> <span className={classes.codeHeader}>{EXPECTED_HEADERS_STRING}</span>
          </Typography>
        </div>

        <div className={classes.dropzoneContainer}>
          {!file ? (
            <div {...getRootProps()} className={classes.uploadInput}>
              <input {...getInputProps()} disabled={isLoading} />
              <CloudUploadIcon width={32} height={32} className={classes.uploadIcon} />
              <Typography className={classes.dragText}>
                {t`Drag and drop a file here, or`}
              </Typography>
              <Button className={classes.chooseFileBtn}>{t`Choose file`}</Button>
              <Typography className={classes.limitsText}>
                {t`CSV or XLS, up to 5MB, 1,000 rows max`}
              </Typography>
              {errorText && <Typography className={classes.errorText}>{errorText}</Typography>}
            </div>
          ) : (
            <div className={classes.fileAndResultsWrapper}>
              <div className={classes.fileInfoBox}>
                <div className={classes.fileInfoLeft}>
                  <ExcelIcon width={24} height={24} className={classes.fileIcon} />
                  <div>
                    <Typography className={classes.fileName}>{file.name}</Typography>
                    <Typography className={classes.fileDetails}>
                      {parsedRows ? `${parsedRows.length} rows detected` : ''} · {(file.size / 1024).toFixed(1)} KB
                    </Typography>
                  </div>
                </div>
                {!isLoading && !results && (
                  <button className={classes.removeFileBtn} onClick={resetFileState}>
                    <DeleteIcon width={18} height={18} color="#a09ba0" />
                  </button>
                )}
              </div>
              
              {!!displayedRows && (
                <div className={classes.validationSection}>
                  <div className={classes.importingRow}>
                    <div>
                      <Typography className={classes.validationTitle}>
                        {results ? t`Import results` : t`Validation results`}
                      </Typography>
                      {!!aggregateSummary && (
                        <Typography className={classes.summaryText}>
                          {isDone ? t`Import Done Successfully — ${aggregateSummary}` : aggregateSummary}
                        </Typography>
                      )}
                    </div>
                    {isLoading &&
                      <div className={classes.importingIndicator}>
                        <Spinner size={16} />
                      </div>}
                  </div>
                  <div className={classes.resultsContainer}>
                    {displayedRows.map((row: IResultMessage, index: number) => {
                      const isGood = row.type === 'good';
                      const isWarn = row.type === 'warning';
                      const isError = row.type === 'error';
                      return (
                        <div key={index} className={clsx(classes.resultRow, {
                          [classes.resultGood]: isGood,
                          [classes.resultWarn]: isWarn,
                          [classes.resultError]: isError,
                        })}>
                          <Typography className={classes.resultText}>
                            {row.message}
                          </Typography>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </BaseDialog>
  );
};

export default ImportTenantListModal;
